import GuestType, { GuestTypes, GuestTypesEnum } from '@/types/taxcard/GuestType';
import Salutation from '@/types/taxcard/Salutation';
import moment, { Moment } from 'moment';
import { ValidationError, Validator } from 'class-validator';
import AdditionalMandatoryField from '@/types/validation/AdditionalMandatoryField';
import i18next from 'i18next';
import AccommodationProjection from '@/types/projections/AccommodationProjection';
import AdditionalGuestTypeFields from './AdditionalGuestTypeFields';
import { getMomentMidday, momentEndDay, momentStartDay } from '@/views/utils/timeCalculations';

//Booking Item Object, since we have different types of cards now being offered by the system we change this from taxcard to a generic object
export default class BIObjectData {
  public guestType: GuestType;
  public subGuestType: string;
  public isAnonymous: boolean;
  public salutation: Salutation;
  public title: string;
  public firstName: string;
  public lastName: string;
  public birthday: Moment | null;
  public addressStreetAndNumber: string;
  public addressZipCode: string;
  public addressCity: string;
  public addressCountry: string;
  public nationality: string;
  public cellphoneNumber: string;
  public email: string;
  public petCount: number;
  public childCount: number;
  public personCount: number;
  public stayBegin: Moment;
  public stayEnd: Moment;
  public option: { [fieldId: string]: any };
  public groupOption: { [fieldId: string]: any };
  public isAnnual: boolean;
  public annualCardKey: string;
  public annualCardResidenceType: string;
  public annualCardPhoto: string;
  public savePersonData: boolean;
  public accommodation: AccommodationProjection;
  public accommodationsProvidersBookingNo: string | null;
  public donationValue: number;

  public errors: ValidationError[] = [];

  public constructor(
    guestType?: GuestType,
    subGuestType?: string,
    isAnonymous?: boolean,
    salutation?: Salutation,
    title?: string,
    firstName?: string,
    lastName?: string,
    birthday?: Moment,
    addressStreetAndNumber?: string,
    addressZipCode?: string,
    addressCity?: string,
    addressCountry?: string,
    nationality?: string,
    cellphoneNumber?: string,
    email?: string,
    petCount?: number,
    personCount?: number,
    stayBegin?: Moment,
    stayEnd?: Moment,
    isAnnual?: boolean,
    annualCardKey?: string,
    annualCardResidenceType?: string,
    annualCardPhoto?: string,
    savePersonData?: boolean,
    option?: {},
    groupOption?: {},
    accommodation?: AccommodationProjection | {},
    accommodationsProvidersBookingNo?: string,
    childCount?: number,
    donationValue?: number,
  ) {
    this.guestType = guestType || GuestTypesEnum.overnight;
    this.subGuestType = subGuestType || '';
    this.isAnonymous = isAnonymous || false;
    this.salutation = salutation || '';
    this.title = title || '';
    this.firstName = firstName || '';
    this.lastName = lastName || '';
    this.birthday = birthday || null;
    this.addressStreetAndNumber = addressStreetAndNumber || '';
    this.addressZipCode = addressZipCode || '';
    this.addressCity = addressCity || '';
    this.addressCountry = addressCountry || 'DE';
    this.nationality = nationality || 'DE'
    this.cellphoneNumber = cellphoneNumber || '';
    this.email = email || '';
    this.petCount = petCount || 0;
    this.personCount = personCount || 0;
    this.stayBegin = stayBegin || momentStartDay(moment());
    this.stayEnd = stayEnd || (this.guestType === GuestTypesEnum.overnight ? momentEndDay(moment()).add(1, 'day') : momentEndDay(moment()));
    this.isAnnual = isAnnual || false;
    this.annualCardKey = annualCardKey || '';
    this.annualCardResidenceType = annualCardResidenceType || '';
    this.annualCardPhoto = annualCardPhoto || '';
    this.savePersonData = savePersonData || false;
    this.option = option || {};
    this.groupOption = groupOption || {};
    this.accommodation = accommodation || {};
    this.accommodationsProvidersBookingNo = accommodationsProvidersBookingNo || null;
    this.childCount = childCount || 0;
    this.donationValue = donationValue || guestType == GuestTypesEnum.donation ? 5 : 0; 
  }

  public validate(mandatoryFieldsToValidate: string[]): boolean {
    this.errors = [];
    const validator = new Validator();
    const t = i18next.t.bind(i18next);
    //Default validations
    if (validator.isEmpty(this.guestType)) {
      this.addError('guestType', 'isEmpty', t('taxcard.validation.guestType.isEmpty'));
    }
    if (!validator.isIn(this.guestType, [...GuestTypes])) {
      this.addError('guestType', 'isIn', t('taxcard.validation.guestType.isIn', { guestType: this.guestType }));
    }

    //Mandatory Fields Validation
    if (mandatoryFieldsToValidate.length) {
      mandatoryFieldsToValidate.forEach((mandatoryFieldToValidate) => {
        switch (mandatoryFieldToValidate) {
          case 'email':
              if(validator.isEmpty(this.email)) {
                this.addError('email', 'isEmpty', t('taxcard.validation.email.isEmpty'));
              }
            break;
          case 'donationValue':
              const donationValueRegex = /^([1-9]\d*)$/;
              if (this.donationValue <= 0 || !donationValueRegex.test(this.donationValue.toString())) {
                this.addError('donationValue', 'isEmpty', t('taxcard.validation.donationValue.isValue'));
              }
            break;
          case 'isAnonymous-false':
              if (this.isAnonymous) {
                this.addError('isAnonymous', 'value', t('taxcard.validation.isAnonymous.value'));
              }
            break;
          case 'salutation':
              if (validator.isEmpty(this.salutation)) {
                this.addError('salutation', 'isEmpty', t('taxcard.validation.salutation.isEmpty'));
              }
            break;
          case 'stayBegin':
              if (!this.stayBegin || !this.stayBegin.isValid()) {
                this.addError('stayBegin', 'isEmpty', t('taxcard.validation.stayBegin.isEmpty'));
              }
            break;
          case 'stayEnd':
              if (!this.stayEnd || !this.stayEnd.isValid()) {
                this.addError('stayEnd', 'isEmpty', t('taxcard.validation.stayEnd.isEmpty'));
              }
            break;
          case 'stayEnd-after-stayBegin':
              if (this.stayBegin && this.stayEnd) {
                if (moment(this.stayEnd).isBefore(moment(this.stayBegin), 'day')){
                  this.addError('stayEnd', 'isPast', t('taxcard.validation.stayEnd.beforeBeginOrOvernightEqual'));
                }
              }
            break;
          case 'stayBegin-notSame-stayEnd':
              if (this.stayBegin && this.stayEnd) {
                if (moment(this.stayEnd).isSame(moment(this.stayBegin), 'day')) {
                  this.addError('stayEnd', 'isPast', t('taxcard.validation.stayEnd.beforeBeginOrOvernightEqual'));
                }
              }
            break;
          case 'stayBegin-stayEnd-maxYear':
              if (this.stayBegin && this.stayEnd && moment(this.stayEnd).diff(moment(this.stayBegin), 'days', true) >= 367.0) {
                this.addError('stayEnd', 'malformed', t("taxcard.maxYear.warning"));
              }
            break;
          case 'accommodation':
              if (validator.isEmpty(this.accommodation) || Object.keys(this.accommodation).length === 0) {
                this.addError('accommodation', 'isEmpty', t('taxcard.validation.accommodation.isEmpty'));
              }

            break;
          case 'annualCardResidenceType':
              if (validator.isEmpty(this.annualCardResidenceType)) {
                this.addError('annualCardResidenceType', 'isEmpty', t('taxcard.validation.annualCardResidenceType.isEmpty'));
              }
            break;
          case 'annualYear':
              if (!this.stayBegin.isValid() || !this.stayEnd.isValid()) {
                this.addError('annualYear', 'isEmpty', t('taxcard.validation.annualYear.isEmpty'));
              }
            break;
          case 'annualCardPhoto':
              if (!this.annualCardPhoto || validator.isEmpty(this.annualCardPhoto)) {
                this.addError('annualCardPhoto', 'isEmpty', t('taxcard.validation.annualCardPhoto.isEmpty'));
              }
            break;
          case 'firstName':
              if (validator.isEmpty(this.firstName)) {
                this.addError('firstName', 'isEmpty', t('taxcard.validation.firstName.isEmpty'));
              }
            break;
          case 'lastName':
              if (validator.isEmpty(this.lastName)) {
                this.addError('lastName', 'isEmpty', t('taxcard.validation.lastName.isEmpty'));
              }
            break;
          case 'birthday':
              if(validator.isEmpty(this.birthday)) {
                this.addError('birthday', 'isEmpty', t('taxcard.validation.birthday.isEmpty'));
              }
            break;
          case 'addressStreetAndNumber':
              if(validator.isEmpty(this.addressStreetAndNumber)) {
                this.addError('addressStreetAndNumber', 'isEmpty', t('taxcard.validation.addressStreetAndNumber.isEmpty'));
              }
            break;
          case 'addressZipCode':
              if(validator.isEmpty(this.addressZipCode)) {
                this.addError('addressZipCode', 'isEmpty', t('taxcard.validation.addressZipCode.isEmpty'));
              }
            break;
          case 'addressCity':
              if(validator.isEmpty(this.addressCity)) {
                this.addError('addressCity', 'isEmpty', t('taxcard.validation.addressCity.isEmpty'));
              }
            break;
          case 'addressCountry':
              if(validator.isEmpty(this.addressCountry)) {
                this.addError('addressCountry', 'isEmpty', t('taxcard.validation.addressCountry.isEmpty'));
              }
            break;
          case 'cellphoneNumber':
              if(validator.isEmpty(this.cellphoneNumber)) {
                this.addError('cellphoneNumber', 'isEmpty', t('taxcard.validation.cellphoneNumber.isEmpty'));
              }
            break;
          case 'nationality':
              if(validator.isEmpty(this.nationality)) {
                this.addError('nationality', 'isEmpty', t('taxcard.validation.nationality.isEmpty'));
              }
            break;            
          default:
            break;
        }
      });
    }

    //Generic Fields Validation
    //Salutation
    if (this.salutation && !validator.isIn(this.salutation, ['', 'MR', 'MRS', 'NONE'])) {
      this.addError('salutation', 'isIn', t('taxcard.validation.salutation.isIn', { salutation: this.salutation }));
    }
    //Cellphonenumber
    if (this.cellphoneNumber && !validator.isNumberString(this.cellphoneNumber)) {
      this.addError('cellphoneNumber', 'isNumberString', t('taxcard.validation.cellphoneNumber.isNumberString'));
    }
    //Birthday
    if (this.birthday !== null && !moment.utc(this.birthday).isValid()) {
      this.addError('birthday', 'malformed', t('taxcard.validation.birthday.notValid'));
    }
    //Email
    if (this.email && !validator.isEmail(this.email)) {
      this.addError('email', 'isEmail', t('taxcard.validation.email.isEmail'));
    }
    // Accommodation Check if accommodation has been deleted or deactived
    if (this.accommodation && (this.accommodation as any).id && !(this.accommodation as any).active) {
      this.addError('accommodation', 'isEmpty', t('taxcard.validation.accommodation.isDeactivated'));
    }
    //AnnualCardResidenceType
    if (this.annualCardResidenceType && !validator.isIn(this.annualCardResidenceType, ['', 'secondary_residence', 'pitch', 'long_term_stay', 'berth'])) {
      this.addError('annualCardResidenceType', 'isIn', t('taxcard.validation.annualCardResidenceType.isIn', { annualCardResidenceType: this.annualCardResidenceType }));
    }

    return false;
  }



  public addError(property: string, constraintType: string, message: string): void {
    const errorExisting = this.errors.find(err => err.property === property);
    if (errorExisting) {
      errorExisting.constraints[constraintType] = message;
    } else {
      this.errors.push({
        property,
        constraints: { [constraintType]: message },
        children: [],
      });
    }
  }

  public dehydrate(): string {
    const simpleObject = {
      ...this,
      birthday: this.birthday ? this.birthday.toISOString() : undefined,
      stayBegin: this.stayBegin ? this.stayBegin.toISOString() : undefined,
      stayEnd: this.stayEnd ? this.stayEnd.toISOString() : undefined,
    };
    return JSON.stringify(simpleObject);
  }

  public static hydrate(jsonObject: any): BIObjectData | null {
    // const data = JSON.parse(json);
    if (!jsonObject) {
      return null;
    }
    return new BIObjectData(
      jsonObject.guestType,
      jsonObject.subGuestType,
      jsonObject.isAnonymous,
      jsonObject.salutation,
      jsonObject.title,
      jsonObject.firstName,
      jsonObject.lastName,
      jsonObject.birthday ? moment.utc(jsonObject.birthday) : undefined,
      jsonObject.addressStreetAndNumber,
      jsonObject.addressZipCode,
      jsonObject.addressCity,
      jsonObject.addressCountry,
      jsonObject.nationality,
      jsonObject.cellphoneNumber,
      jsonObject.email,
      jsonObject.petCount ? parseInt(jsonObject.petCount, 10) : 0,
      jsonObject.personCount ? parseInt(jsonObject.personCount, 10) : 0,
      jsonObject.stayBegin ? moment(jsonObject.stayBegin) : undefined,
      jsonObject.stayEnd ? moment(jsonObject.stayEnd) : undefined,
      jsonObject.isAnnual,
      jsonObject.annualCardKey,
      jsonObject.annualCardResidenceType,
      jsonObject.annualCardPhoto,
      jsonObject.savePersonjsonObject,
      jsonObject.option,
      jsonObject.groupOption,
      jsonObject.accommodation,
      jsonObject.accommodationsProvidersBookingNo,
      jsonObject.childCount,
      jsonObject.donationValue
    );
  }
}
